import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Wallet, Settings as SettingsIcon, LifeBuoy, X } from 'lucide-react';

/**
 * MissionControlNavRail — single source of truth for the Mission
 * Control navigation destinations. Rendered in two variants:
 *
 *   variant="inline"   — MissionControl main page. Fold-out left rail
 *                        that sits inside the flex layout; width
 *                        toggles between 14 (icons) and 56 (icons +
 *                        labels).
 *
 *   variant="overlay"  — Teaser / holding pages. Fixed drawer that
 *                        slides in from the left when open; renders
 *                        as icons + labels with a close button.
 *
 * Both variants use the same destinations list and same styling
 * language so behaviour and appearance stay in lock-step.
 */
export const NAV_DESTINATIONS = [
  { id: 'mission-control', label: 'Mission Control', href: '/mission-control', icon: Home },
  { id: 'wallet',          label: 'My Wallet',       href: '/soltracker/dashboard', icon: Wallet },
  { id: 'settings',        label: 'Settings',        href: '/settings',        icon: SettingsIcon },
  { id: 'help',            label: 'Help',            href: '#help',            icon: LifeBuoy },
];

const RailLink = ({ id, label, href, icon: Icon, showLabel, onClick }) => (
  <Link
    key={id}
    to={href.startsWith('#') ? '/mission-control' : href}
    onClick={onClick}
    className={`mx-2 my-1 flex items-center gap-3 rounded-lg px-3 py-2 text-white/60 hover:text-white hover:bg-white/5 transition ${!showLabel ? 'justify-center' : ''}`}
    data-testid={`nav-${id}`}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    {showLabel && <span className="text-sm truncate" style={{ fontFamily: 'Space Grotesk' }}>{label}</span>}
  </Link>
);

const MissionControlNavRail = ({ variant = 'inline', open, onClose }) => {
  if (variant === 'inline') {
    // MissionControl embedded rail — desktop only, collapses to w-14.
    return (
      <aside
        className={`hidden md:flex flex-col border-r border-white/8 bg-[#080B12]/70 backdrop-blur-md ${open ? 'w-56' : 'w-14'} transition-all duration-200 py-3`}
        data-testid="left-nav-rail"
      >
        {NAV_DESTINATIONS.map((d) => (
          <RailLink key={d.id} {...d} showLabel={open} />
        ))}
      </aside>
    );
  }

  // Overlay variant — teaser / holding pages. Fixed drawer that
  // slides in from the left. Includes a backdrop + close button so
  // it works on desktop AND mobile (teaser hamburger is always
  // visible, unlike the MissionControl rail which is desktop-only).
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        data-testid="nav-rail-backdrop"
      />
      {/* Drawer */}
      <aside
        aria-hidden={!open}
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#080B12] border-r border-white/8 shadow-2xl transform transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="nav-rail-drawer"
      >
        <div className="p-4 border-b border-white/8 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: 'Space Grotesk' }}>Degens DAO</span>
          <button
            onClick={onClose}
            data-testid="nav-rail-close"
            aria-label="Close navigation"
            className="p-1.5 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="py-3">
          {NAV_DESTINATIONS.map((d) => (
            <RailLink key={d.id} {...d} showLabel onClick={onClose} />
          ))}
        </nav>
      </aside>
    </>
  );
};

export default MissionControlNavRail;
